import {Injectable} from '@angular/core';
import {BehaviorSubject, Subject} from 'rxjs';
import {GraphicUndersampling} from '../../enum/graphic-undersampling.enum';
import {State} from '../../classes/graphic-component-view/graphic-state';
import {GraphicGrouping} from '../../enum/graphic-grouping.enum';
import {Helper} from '../../helper/helper';
import {cloneDeep} from 'lodash';
import {LocationsFormatterService} from '../project-measure/locations-formatter.service';
import {MergeServerClientLocationsService} from '../project-measure/merge-server-client-locations.service';
import {Location} from '../../types/location.type';

const SMART_OFFSET_MAX_TIME_RANGE = 24;
const SMART_OFFSET_MAX_NUMBER_POINTS = 144;

@Injectable({
  providedIn: 'root'
})
export class ProjectMeasureGraphicStateService {

  isSmartOffsetModeChecked$: BehaviorSubject<State>;
  isSmartOffsetModeDisabled$: BehaviorSubject<State>;
  locationsInitedOrChanged$: BehaviorSubject<Location.clientSide[]>;
  locationToggled$: BehaviorSubject<[Location.clientSide[], boolean | null, string[] | null]>;
  loadGraph$: Subject<boolean>;
  unitToogled$: Subject<[string, boolean]>;
  persistViewAfterLocationsChange$: Subject<any>;
  private _state: State;
  private _updates: State;
  private _retainedValues: State;

  constructor(private locationsFormatterService: LocationsFormatterService,
              private mergeServerClientLocationsService: MergeServerClientLocationsService) {
  }

  initState() {
    this._state = new State();
    this._retainedValues = {};
    this.isSmartOffsetModeDisabled$ = new BehaviorSubject(this._state);
    this.isSmartOffsetModeChecked$ = new BehaviorSubject(this._state);
    this.locationsInitedOrChanged$ = new BehaviorSubject(this._state.locations);
    this.locationToggled$ = new BehaviorSubject([this._state.locations, null, null]);
    this.loadGraph$ = new Subject();
    this.unitToogled$ = new Subject();
    this.persistViewAfterLocationsChange$ = new Subject();
  }

  // 'shortcut' to give to possibility to directly update a state property from components
  public updateState(updates: State) {
    updates = cloneDeep(updates);
    if (updates.hasOwnProperty('periodSelectedForDataGrouping')) {    // temporary hack to use different name in state and graph views
      // @ts-ignore
      updates.dataGroupingPeriod = updates.periodSelectedForDataGrouping;
      // @ts-ignore
      delete updates.periodSelectedForDataGrouping;
    }
    switch (true) {
      case updates.hasOwnProperty('showThermalCorrection'):
        this.updateStateFromAction('thermalCorrectionToggled', updates.showThermalCorrection);
        break;
      case updates.hasOwnProperty('smartOffsetModeIsChecked'):
        this.updateStateFromAction('smartOffsetModeToggled', updates.smartOffsetModeIsChecked);
        break;
      default:
        this.updateStateFromAction('graphicViewChanged', updates);
    }
  }

  public updateStateFromAction(action, value) {
    this.resetUpdates();
    let updates;
    switch (action) {
      case 'thermalCorrectionToggled':
        updates = this.computeUpdateOnThermalCorrectionToggled(value);
        break;
      case 'smartOffsetModeToggled':
        updates = this.computeUpdateOnSmartOffsetsModeToggled(value);
        break;
      case 'graphicViewChanged':
        updates = this.completeUpdatesOnGraphicViewChanged(value);
        break;
      case 'newMeasures':
        updates = this.computeUpdateOnSeriesPointNumberChange(value);
        break;
    }

    this._updates = Object.assign(this._updates, updates);
    this.createNewState().dispatchNewState();
  }

  setStateFromView(view: State) {
    let locations = view.locations;
    if (this._state.locationsFromServer) {
      locations = this.mergeServerClientLocationsService.buildLocations(this._state.showThermalCorrection, this._state.locationsFromServer, locations, view.locationSelected);
    }
    this.updateState(Object.assign(view, {locations}));
    this.locationsInitedOrChanged$.next(this.get('locations'));
  }

  setLocationsLoadedFromServer(serverLocations: Location.serverSide[]) {
    const mergedLocations = this.mergeServerClientLocationsService.buildLocations(this._state.showThermalCorrection, serverLocations, this._state.locations, this._state.locationSelected);
    this.updateState({locationsFromServer: serverLocations, locations: mergedLocations});
    this.locationsInitedOrChanged$.next(cloneDeep(mergedLocations));
  }

  setLocationSelected(locations: Location.clientSide[], selected = null, seriesName = null) {
    // 'hack' to delegate expensive computation to the next loop tick
    setTimeout(() => {
      this.updateState({locations});
      this.locationToggled$.next([cloneDeep(locations), selected, seriesName]);
    }, 0);
  }

  updateLocations(locations: Location.clientSide[]) {
    // 'hack' to delegate expensive computation to the next loop tick
    setTimeout(() => {
      this.updateState({locations});
      this.persistViewAfterLocationsChange$.next();
    }, 0);
  }

  get(property: string) {
    // todo: add check
    const value = this._state ? this._state[property] : null;
    if (typeof value === 'object') {
      return cloneDeep(value);
    } else {
      return value;
    }
  }

  getLocation(id: number) {
    if (!this._state.locationsFromServer) {
      throw new Error('Project device locations have not been set in state.');
    }
    return cloneDeep(this._state.locationsFromServer.find((location: any) => id === location.id));
  }

  private createNewState() {
    this._state = Object.assign({}, this._state, this._updates);

    return this;
  }

  private dispatchNewState() {
    if (this._state.smartOffsetModeIsChecked !== this.isSmartOffsetModeChecked$.getValue().smartOffsetModeIsChecked) {
      this.isSmartOffsetModeChecked$.next(this._state);
    }
    if (this._state.smartOffsetModeIsDisabled !== this.isSmartOffsetModeDisabled$.getValue().smartOffsetModeIsDisabled) {
      this.isSmartOffsetModeDisabled$.next(this._state);
    }
  }

  private computeUpdateOnThermalCorrectionToggled(isChecked: boolean) {
    const updates: State = {};
    updates.showThermalCorrection = isChecked;
    updates.smartOffsetModeIsDisabled = true;
    updates.smartOffsetStateInfo = 'SMART_OFFSET_MODE_DISABLED_INFO_THERMAL_CORRECTION';

    return updates;
  }

  private completeUpdatesOnGraphicViewChanged(updates: State) {

    if (!this._state.smartOffsetModeIsChecked) {     // if the mode is not selected we do nothing more
      return updates;
    }

    switch (true) {
      case updates.showEvents:
        this.completeUpdatesOnSmartOffsetModeIsTurnedOff(updates);
        updates.informUser = 'SMART_OFFSET_MODE_DISABLED_DUE_TO_DYNAMICS';
        break;
      case updates.showOffset === false:
        this.completeUpdatesOnSmartOffsetModeIsTurnedOff(updates);
        updates.informUser = 'SMART_OFFSET_MODE_DISABLED_DUE_TO_OFFSETS';
        break;
      case updates.underSampling === GraphicUndersampling.fast:
        this.completeUpdatesOnSmartOffsetModeIsTurnedOff(updates);
        updates.informUser = 'SMART_OFFSET_MODE_DISABLED_DUE_TO_FAST_UNDERSAMPLING';
        updates.reloadChart = true;
        break;
      case updates.dataGroupingPeriod && updates.dataGroupingPeriod !== GraphicGrouping.none:
        this.completeUpdatesOnSmartOffsetModeIsTurnedOff(updates);
        updates.informUser = 'SMART_OFFSET_MODE_DISABLED_DUE_TO_DATA_GROUPING';
    }

    return updates;
  }

  private computeUpdateOnSmartOffsetsModeToggled(isChecked: boolean) {
    const updates: State = {};
    updates.smartOffsetModeIsChecked = isChecked;
    updates.reloadChart = true;
    if (updates.smartOffsetModeIsChecked) {
      updates.switchSmartOffsetsNotification = 'open';
      this.storeValuesToRetain();
      updates.underSampling = GraphicUndersampling.raw;
      updates.dataGroupingPeriod = GraphicGrouping.none;
      updates.showEvents = false;
      updates.showOffset = true;
    } else {
      updates.switchSmartOffsetsNotification = 'close';
      this.restoreValuesChangedBySmartOffsetMode(updates);
    }

    return updates;
  }

  private computeUpdateOnSeriesPointNumberChange(measures: any[]) {
    const updates: State = {};

    const isValidPointNumber = () => {
      const lengths: number[] = measures.map((d) => d.measures.length);
      return Math.max(...lengths) < SMART_OFFSET_MAX_NUMBER_POINTS;
    };

    const isValidTime = () => {
      const _start = this._state.datePickerStart;
      const _end = this._state.datePickerEnd;
      if (!_start || !_end) {
        return false;
      }
      const start = (new Date(_start)).getTime();
      const end = (new Date(_end)).getTime();
      return (end - start) < (SMART_OFFSET_MAX_TIME_RANGE * 3600 * 1000);
    };

    if (isValidPointNumber() || isValidTime()) {
      updates.smartOffsetModeIsDisabled = false;
    } else {
      updates.smartOffsetModeIsDisabled = true;
      updates.smartOffsetStateInfo = 'SMART_OFFSET_MODE_DISABLED_INFO_TIME_RANGE_OR_POINTS';
      if (this._state.smartOffsetModeIsChecked) {
        this.completeUpdatesOnSmartOffsetModeIsTurnedOff(updates, 'SMART_OFFSET_MODE_DISABLED_DUE_TO_TIME_RANGE_OR_POINTS', false);
      }
    }

    return updates;
  }

  private completeUpdatesOnSmartOffsetModeIsTurnedOff(updates: State, messageToInformUser = '', restoreValues = true) {
    updates.smartOffsetModeIsChecked = false;
    updates.switchSmartOffsetsNotification = 'close';
    updates.informUser = messageToInformUser;
    if (restoreValues) {
      this.restoreValuesChangedBySmartOffsetMode(updates);
    } else {
      updates.underSampling = this._retainedValues.underSampling || GraphicUndersampling.fast;
    }
  }

  private storeValuesToRetain() {
    this._retainedValues.dataGroupingPeriod = this._state.dataGroupingPeriod;
    this._retainedValues.showEvents = this._state.showEvents;
    this._retainedValues.showOffset = this._state.showOffset;
    this._retainedValues.underSampling = this._state.underSampling;
  }

  private restoreValuesChangedBySmartOffsetMode(updates) {
    updates.dataGroupingPeriod = Helper.getFirstDefinedValue(updates.dataGroupingPeriod, this._retainedValues.dataGroupingPeriod,
      GraphicGrouping.none);
    updates.showEvents = Helper.getFirstDefinedValue(updates.showEvents, this._retainedValues.showEvents, false);
    updates.showOffset = Helper.getFirstDefinedValue(updates.showOffset, this._retainedValues.showOffset, true);
    updates.underSampling = Helper.getFirstDefinedValue(updates.underSampling, this._retainedValues.underSampling,
      GraphicUndersampling.fast);
  }

  private resetUpdates() {
    this._updates = {};
    this._updates.reloadChart = false;  // Excepted for Smart Offsets Mode, we leave the Graph component handle itself when to reload the graph
    this._updates.informUser = '';
    this._updates.smartOffsetStateInfo = '';
    this._updates.switchSmartOffsetsNotification = null;

    return this;
  }
}
